import React, { useState, useEffect } from 'react';
import { Table, Button, message, Popconfirm, Tag, Input } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { getSubcontractorEmployees, deleteSubcontractorEmployee } from '../api';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

const getDocExpiry = (docs, types) => {
  if (!docs || !Array.isArray(docs)) return null;
  const doc = docs.find(d => types.includes(d.document_type));
  return doc && doc.expiry_date ? doc.expiry_date : null;
};

const renderExpiryTag = (dateStr) => {
  if (!dateStr) return <span style={{ color: '#888' }}>N/A</span>;
  const today = moment();
  const exp = moment(dateStr);
  let color = 'green';
  if (exp.isBefore(today, 'day')) color = 'red';
  else if (exp.diff(today, 'days') <= 30) color = 'orange';
  return <Tag color={color}>{exp.format('DD/MM/YYYY')}</Tag>;
};

const SubcontractorEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const navigate = useNavigate();
  const { subcontractorId } = useParams();

  const fetchEmployees = async () => {
    if (!subcontractorId) return;
    setLoading(true);
    const res = await getSubcontractorEmployees(subcontractorId);
    setLoading(false);
    if (res.success) {
      setEmployees(res.data);
    } else {
      message.error(res.message || 'Failed to fetch employees');
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [subcontractorId]);

  const handleDelete = async (id) => {
    const res = await deleteSubcontractorEmployee(id);
    if (res.success) {
      message.success('Employee deleted');
      fetchEmployees();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      (emp.name || '').toLowerCase().includes(s) ||
      (emp.emp_code || '').toLowerCase().includes(s) ||
      (emp.designation || '').toLowerCase().includes(s) ||
      (emp.phone || '').toLowerCase().includes(s)
    );
  });

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 60,
      render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1
    },
    { title: 'EID', dataIndex: 'emp_code', key: 'emp_code', render: val => val || 'N/A' },
    {
      title: 'EID Expiry Date',
      key: 'eid_expiry',
      render: (_, record) => renderExpiryTag(getDocExpiry(record.documents, ['emirates_id', 'emirates_id_back']))
    },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Designation', dataIndex: 'designation', key: 'designation', render: val => val || 'N/A' },
    {
      title: 'Passport Expiry Date',
      key: 'passport_expiry',
      render: (_, record) => renderExpiryTag(getDocExpiry(record.documents, ['passport']))
    },
    {
      title: 'Work Permit Expiry Date',
      key: 'work_permit_expiry',
      render: (_, record) => renderExpiryTag(getDocExpiry(record.documents, ['labour_card', 'work_permit']))
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button size="small" icon={<EyeOutlined />} style={{ marginRight: 8 }} onClick={() => navigate(`/subcontractors/${subcontractorId}/employees/${record.id}/view`)} />
          <Button size="small" icon={<EditOutlined />} style={{ marginRight: 8 }} onClick={() => navigate(`/subcontractors/${subcontractorId}/employees/${record.id}/edit`)} />
          <Popconfirm title="Are you sure to delete?" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3>Employees</h3>
        <div style={{ display: 'flex', gap: 12 }}>
          <Input.Search
            placeholder="Search employee by name, code, designation..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Button type="primary" onClick={() => navigate(`/subcontractors/${subcontractorId}/employees/add`)}>
            Add Employee
          </Button>
        </div>
      </div>
      <Table
        dataSource={filteredEmployees}
        columns={columns}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={pagination}
        onChange={(p) => setPagination(p)}
      />
    </div>
  );
};

export default SubcontractorEmployees; 